import React, { useState, useEffect } from 'react';


// Hindi phonemes organized by type.
// Exported for the Case History inventory grid (§9), which needs the same 44
// sounds as a multi-select rather than this component's single-value dropdown.
export const HINDI_PHONEMES = {
    consonants: {
        stops: [
            { ipa: 'p', display: 'प /p/', devanagari: 'प' },
            { ipa: 'b', display: 'ब /b/', devanagari: 'ब' },
            { ipa: 'pʰ', display: 'फ /pʰ/', devanagari: 'फ' },
            { ipa: 'bʱ', display: 'भ /bʱ/', devanagari: 'भ' },
            { ipa: 't', display: 'त /t/', devanagari: 'त' },
            { ipa: 'd', display: 'द /d/', devanagari: 'द' },
            { ipa: 'tʰ', display: 'थ /tʰ/', devanagari: 'थ' },
            { ipa: 'dʱ', display: 'ध /dʱ/', devanagari: 'ध' },
            { ipa: 'ʈ', display: 'ट /ʈ/', devanagari: 'ट' },
            { ipa: 'ɖ', display: 'ड /ɖ/', devanagari: 'ड' },
            { ipa: 'ʈʰ', display: 'ठ /ʈʰ/', devanagari: 'ठ' },
            { ipa: 'ɖʱ', display: 'ढ /ɖʱ/', devanagari: 'ढ' },
            { ipa: 'k', display: 'क /k/', devanagari: 'क' },
            { ipa: 'ɡ', display: 'ग /ɡ/', devanagari: 'ग' },
            { ipa: 'kʰ', display: 'ख /kʰ/', devanagari: 'ख' },
            { ipa: 'ɡʱ', display: 'घ /ɡʱ/', devanagari: 'घ' },
            { ipa: 'tʃ', display: 'च /tʃ/', devanagari: 'च' },
            { ipa: 'dʒ', display: 'ज /dʒ/', devanagari: 'ज' },
            { ipa: 'tʃʰ', display: 'छ /tʃʰ/', devanagari: 'छ' },
            { ipa: 'dʒʱ', display: 'झ /dʒʱ/', devanagari: 'झ' }
        ],
        nasals: [
            { ipa: 'm', display: 'म /m/', devanagari: 'म' },
            { ipa: 'n', display: 'न /n/', devanagari: 'न' },
            { ipa: 'ɳ', display: 'ण /ɳ/', devanagari: 'ण' },
            { ipa: 'ŋ', display: 'ङ /ŋ/', devanagari: 'ङ' }
        ],
        fricatives: [
            { ipa: 's', display: 'स /s/', devanagari: 'स' },
            { ipa: 'ʃ', display: 'श /ʃ/', devanagari: 'श' },
            { ipa: 'h', display: 'ह /h/', devanagari: 'ह' },
            { ipa: 'z', display: 'ज़ /z/', devanagari: 'ज़' },
            { ipa: 'f', display: 'फ़ /f/', devanagari: 'फ़' }
        ],
        liquids: [
            { ipa: 'r', display: 'र /r/', devanagari: 'र' },
            { ipa: 'l', display: 'ल /l/', devanagari: 'ल' },
            { ipa: 'ɭ', display: 'ळ /ɭ/', devanagari: 'ळ' },
            { ipa: 'v', display: 'व /v/', devanagari: 'व' },
            { ipa: 'j', display: 'य /j/', devanagari: 'य' }
        ]
    },
    vowels: [
        { ipa: 'ə', display: 'अ /ə/', devanagari: 'अ' },
        { ipa: 'aː', display: 'आ /aː/', devanagari: 'आ' },
        { ipa: 'ɪ', display: 'इ /ɪ/', devanagari: 'इ' },
        { ipa: 'iː', display: 'ई /iː/', devanagari: 'ई' },
        { ipa: 'ʊ', display: 'उ /ʊ/', devanagari: 'उ' },
        { ipa: 'uː', display: 'ऊ /uː/', devanagari: 'ऊ' },
        { ipa: 'eː', display: 'ए /eː/', devanagari: 'ए' },
        { ipa: 'ɛː', display: 'ऐ /ɛː/', devanagari: 'ऐ' },
        { ipa: 'oː', display: 'ओ /oː/', devanagari: 'ओ' },
        { ipa: 'ɔː', display: 'औ /ɔː/', devanagari: 'औ' }
    ]
};

// English phonemes
const ENGLISH_PHONEMES = {
    consonants: {
        stops: [
            { ipa: 'p', display: 'p (pat)', example: 'pat' },
            { ipa: 'b', display: 'b (bat)', example: 'bat' },
            { ipa: 't', display: 't (tap)', example: 'tap' },
            { ipa: 'd', display: 'd (dog)', example: 'dog' },
            { ipa: 'k', display: 'k (cat)', example: 'cat' },
            { ipa: 'ɡ', display: 'g (go)', example: 'go' }
        ],
        fricatives: [
            { ipa: 'f', display: 'f (fish)', example: 'fish' },
            { ipa: 'v', display: 'v (van)', example: 'van' },
            { ipa: 'θ', display: 'th (think)', example: 'think' },
            { ipa: 'ð', display: 'th (this)', example: 'this' },
            { ipa: 's', display: 's (sun)', example: 'sun' },
            { ipa: 'z', display: 'z (zoo)', example: 'zoo' },
            { ipa: 'ʃ', display: 'sh (ship)', example: 'ship' },
            { ipa: 'ʒ', display: 'zh (vision)', example: 'vision' },
            { ipa: 'h', display: 'h (hat)', example: 'hat' }
        ],
        affricates: [
            { ipa: 'tʃ', display: 'ch (church)', example: 'church' },
            { ipa: 'dʒ', display: 'j (judge)', example: 'judge' }
        ],
        nasals: [
            { ipa: 'm', display: 'm (man)', example: 'man' },
            { ipa: 'n', display: 'n (no)', example: 'no' },
            { ipa: 'ŋ', display: 'ng (sing)', example: 'sing' }
        ],
        liquids: [
            { ipa: 'l', display: 'l (let)', example: 'let' },
            { ipa: 'r', display: 'r (red)', example: 'red' },
            { ipa: 'w', display: 'w (wet)', example: 'wet' },
            { ipa: 'j', display: 'y (yes)', example: 'yes' }
        ]
    },
    vowels: [
        { ipa: 'iː', display: 'ee (see)', example: 'see' },
        { ipa: 'ɪ', display: 'i (sit)', example: 'sit' },
        { ipa: 'ɛ', display: 'e (bed)', example: 'bed' },
        { ipa: 'æ', display: 'a (cat)', example: 'cat' },
        { ipa: 'ɑː', display: 'ah (father)', example: 'father' },
        { ipa: 'ɔː', display: 'aw (saw)', example: 'saw' },
        { ipa: 'ʊ', display: 'oo (book)', example: 'book' },
        { ipa: 'uː', display: 'oo (food)', example: 'food' },
        { ipa: 'ʌ', display: 'u (cup)', example: 'cup' },
        { ipa: 'ə', display: 'uh (about)', example: 'about' }
    ]
};

const POSITIONS = [
    { value: 'initial', label: 'Initial' },
    { value: 'medial', label: 'Medial' },
    { value: 'final', label: 'Final' },
    { value: 'syllable', label: 'Syllable' }
];

/**
 * PhonemePicker Component
 * 
 * A reusable component for selecting IPA phonemes with language-specific options.
 * 
 * Props:
 * - language: 'hi-IN' | 'en-US' - Language for phoneme selection
 * - value: string - Current IPA symbol
 * - onChange: (ipa: string) => void - Callback when phoneme changes
 * - position: string - Current position value
 * - onPositionChange: (position: string) => void - Callback when position changes
 * - showPosition: boolean - Whether to show position selector (default: true)
 */
const PhonemePicker = ({
    language = 'hi-IN',
    value = '',
    onChange,
    position = 'initial',
    onPositionChange,
    showPosition = true
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [phonemeData, setPhonemeData] = useState(null);

    useEffect(() => {
        // Set phoneme data based on language
        if (language === 'hi-IN') {
            setPhonemeData(HINDI_PHONEMES);
        } else if (language === 'en-US') {
            setPhonemeData(ENGLISH_PHONEMES);
        }
    }, [language]);

    if (!phonemeData) {
        return <div>Loading phonemes...</div>;
    }

    // Flatten all phonemes for search
    const allPhonemes = [
        ...Object.values(phonemeData.consonants).flat(),
        ...phonemeData.vowels
    ];

    // Filter phonemes based on search
    const filteredPhonemes = searchTerm
        ? allPhonemes.filter(p =>
            p.display.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.ipa.includes(searchTerm)
        )
        : allPhonemes;

    return (
        <div className="phoneme-picker">
            <div className="phoneme-picker-controls">
                <div className="phoneme-select-wrapper">
                    <label>Phoneme:</label>
                    <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        className="phoneme-select"
                    >
                        <option value="">Select phoneme...</option>

                        {/* Consonants */}
                        <optgroup label="Consonants">
                            {Object.entries(phonemeData.consonants).map(([type, phonemes]) => (
                                <React.Fragment key={type}>
                                    <option disabled>── {type.charAt(0).toUpperCase() + type.slice(1)} ──</option>
                                    {phonemes.map(p => (
                                        <option key={p.ipa} value={p.ipa}>
                                            {p.display}
                                        </option>
                                    ))}
                                </React.Fragment>
                            ))}
                        </optgroup>

                        {/* Vowels */}
                        <optgroup label="Vowels">
                            {phonemeData.vowels.map(p => (
                                <option key={p.ipa} value={p.ipa}>
                                    {p.display}
                                </option>
                            ))}
                        </optgroup>
                    </select>
                </div>

                {showPosition && (
                    <div className="position-select-wrapper">
                        <label>Position:</label>
                        <select
                            value={position}
                            onChange={(e) => onPositionChange(e.target.value)}
                            className="position-select"
                        >
                            {POSITIONS.map(pos => (
                                <option key={pos.value} value={pos.value}>
                                    {pos.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {value && (
                <div className="phoneme-preview">
                    <strong>Selected:</strong> {allPhonemes.find(p => p.ipa === value)?.display || value}
                </div>
            )}
        </div>
    );
};

export default PhonemePicker;
